<template>
  <PInputMenu
    v-model="model"
    :open="open"
    :items="menuItems"
    mode="autocomplete"
    value-key="label"
    ignore-filter
    autocomplete="off"
    :placeholder="placeholder"
    :disabled="disabled"
    :loading="loading"
    :trailing="false"
    :open-on-focus="false"
    :open-on-click="false"
    v-bind="inputAttrs"
    @update:model-value="onAddressInput"
    @update:open="onOpenChange"
    @highlight="onHighlight"
    @keydown="handleKeydown"
    @focus="isFocused = true"
    @blur="isFocused = false"
  >
    <template v-if="$slots['before-input']" #leading>
      <slot name="before-input" />
    </template>
    <template #item-leading="{ item }">
      <span class="w-12 shrink-0 text-[10px] tracking-wide uppercase opacity-70">
        {{ item.source === "saved" ? "Opgesl." : "Maps" }}
      </span>
    </template>
    <template #empty>
      <span v-if="trimmedQuery.length < LOCATION_AUTOCOMPLETE_MIN_CHARS">
        Typ minimaal {{ LOCATION_AUTOCOMPLETE_MIN_CHARS }} tekens om een locatie te zoeken…
      </span>
      <span v-else-if="loading">Zoeken…</span>
      <span v-else>Geen locaties gevonden</span>
    </template>
  </PInputMenu>
</template>

<script setup lang="ts">
import { computed, inject, onBeforeUnmount, ref, useAttrs } from "vue"
import { storeToRefs } from "pinia"
import PInputMenu from "@/components/form/PInputMenu.vue"
import useDelayedFunction from "@/composables/input/delayed-function.ts"
import type { LocationController } from "@/controllers/model-controllers/location-controller"
import { useAuthStore } from "@/store/auth.store"
import {
  LOCATION_AUTOCOMPLETE_MIN_CHARS,
  mergeFirstSuggestionStreetWithHouseNumber,
  searchLocationSuggestions,
  shouldApplyFirstSuggestionStreet,
  useSuggestionList,
  type LocationSuggestion
} from "@/composables/location/use-location-autocomplete"

defineOptions({ inheritAttrs: false })

const props = withDefaults(
  defineProps<{
    placeholder?: string
    disabled?: boolean
    /** A location id to keep out of the saved suggestions (e.g. the one being edited). */
    excludeId?: string
  }>(),
  {
    placeholder: "Zoek een adres…",
    disabled: false
  }
)

const emit = defineEmits<{ select: [item: LocationSuggestion] }>()
const model = defineModel<string>({ default: "" })
const isFocused = ref(false)

const attrs = useAttrs()
const inputAttrs = computed(() => {
  const forwarded = { ...(attrs as Record<string, unknown>) }
  delete forwarded.placeholder
  delete forwarded.disabled
  return forwarded
})

const locationController = inject<LocationController>("locationController")
const { activeUser } = storeToRefs(useAuthStore())
const { open, loading, items, highlightedIndex, query, trimmedQuery } = useSuggestionList()
let searchGeneration = 0

const menuItems = computed(() =>
  items.value.map((item) => ({
    ...item,
    onSelect: (event: Event) => {
      // The parent adopts saved addresses or geocodes Maps suggestions itself.
      event.preventDefault()
      pickSuggestion(item)
    }
  }))
)

function onOpenChange(value: boolean) {
  if (value) open.value = true
  else closeMenu()
}

function onHighlight(payload: { value: string } | undefined) {
  if (!payload) return
  const index = items.value.findIndex((item) => item.label === payload.value)
  if (index >= 0) highlightedIndex.value = index
}

async function runSearch(q: string): Promise<LocationSuggestion[]> {
  const generation = ++searchGeneration
  loading.value = true
  try {
    const results = await searchLocationSuggestions(q, {
      locationController,
      apiKey: activeUser.value?.activeTenant?.tenantSettings?.apiKeyGoogleMaps ?? "",
      excludeId: props.excludeId
    })
    if (generation !== searchGeneration || !open.value || query.value.trim() !== q) return []
    items.value = results
    highlightedIndex.value = 0
    return results
  } finally {
    if (generation === searchGeneration) loading.value = false
  }
}

const delayedSearch = useDelayedFunction(async (q: string) => {
  await runSearch(q)
}, 250)

function closeMenu() {
  open.value = false
  items.value = []
  highlightedIndex.value = 0
  delayedSearch.cancel()
  searchGeneration++
  loading.value = false
}

function onAddressInput(value: string | null | undefined) {
  if (!isFocused.value) return
  const next = value ?? ""
  const trimmed = next.trim()
  const lastChar = next.slice(-1)

  if (
    open.value &&
    items.value.length > 0 &&
    /^\d$/.test(lastChar) &&
    shouldApplyFirstSuggestionStreet(trimmed, trimmed.slice(0, -1))
  ) {
    const highlighted = items.value[highlightedIndex.value] ?? items.value[0]
    if (!highlighted) return
    const merged = mergeFirstSuggestionStreetWithHouseNumber(trimmed, highlighted)
    if (merged && merged !== trimmed) {
      query.value = merged
      model.value = merged
      open.value = true
      delayedSearch.schedule(merged)
      return
    }
  }

  query.value = next
  if (!next.trim()) {
    closeMenu()
    return
  }
  open.value = true
  if (next.trim().length < LOCATION_AUTOCOMPLETE_MIN_CHARS) {
    delayedSearch.cancel()
    searchGeneration++
    items.value = []
    loading.value = false
    return
  }
  delayedSearch.schedule(next.trim())
}

function pickSuggestion(item: LocationSuggestion) {
  closeMenu()
  emit("select", item)
}

async function pickHighlightedOrFirstSuggestion() {
  let item = items.value[highlightedIndex.value] ?? items.value[0]
  if (!item && trimmedQuery.value.length >= LOCATION_AUTOCOMPLETE_MIN_CHARS) {
    delayedSearch.cancel()
    const results = await runSearch(trimmedQuery.value)
    item = results[0]
  }
  if (item) await pickSuggestion(item)
}

function handleKeydown(event: KeyboardEvent) {
  if (!open.value || event.isComposing) return

  // Keep Tab selection and immediate Enter searches while Nuxt handles list navigation.
  if (event.key === "Tab" || (event.key === "Enter" && items.value.length === 0)) {
    event.preventDefault()
    void pickHighlightedOrFirstSuggestion()
  }
}

onBeforeUnmount(closeMenu)
</script>
